import {
    contextMenuCallback_getCoordinates,
    contextMenuCallback_getNearestAddress,
    getBingLayer_labels,
    getBingLayer_satellite,
    getGoogleLayer_labels,
    getGoogleLayer_satellite,
    getOsmLayer,
    leftOrMiddleClickPan,
    MapControlClass,
    rotateCanvasAboutPoint,
    searchAddressCallback,
} from '@/js/util/map_functionality.js';

import {
    assertIncludes,
    parseIntStrict,
} from '@/js/util/asserts.js';

import {
    DEFAULT_MAP_CENTER,
    FeatureType,
    HexColor,
    MAP_EXPORT_SETTINGS,
    MAP_EXPORT_SETTINGS_DEFAULTS,
    MAP_SETTINGS_DEFAULTS,
    MapControl,
} from '@/js/util/shared_constants.js';

import {
    CountdownBarrier,
    isMobileDevice,
    loadImage,
} from '@/js/util/shared_functionality.js';

import { ErrorModal, Modal, modalIsOpen } from '@/js/classes/modal.js';
import { AssetForm } from '@/js/classes/asset_form.js';
import { delay, hexToRgba } from '@/js/util.js';

import { Map, View } from 'ol';
import { Attribution as AttributionControl } from 'ol/control';
import { fromLonLat, toLonLat } from 'ol/proj';
import VectorSource from 'ol/source/Vector';
import VectorLayer from 'ol/layer/Vector';
import { Circle, Fill, Stroke, Style, Text } from 'ol/style';
import { Cluster } from 'ol/source';
import { DragRotateAndZoom, DragZoom } from 'ol/interaction';
import { shiftKeyOnly, platformModifierKeyOnly } from 'ol/events/condition';
import { getPointResolution } from 'ol/proj';

import {
    DragPan,
    MouseWheelZoom,
    PinchZoom,
    DoubleClickZoom,
    KeyboardPan,
    KeyboardZoom,
    defaults as defaultInteractions,
} from 'ol/interaction';

import { captureException as sentryCaptureException } from '@sentry/browser';
import { ctxmenu } from 'ctxmenu';


export class MapPage {
    static pan_interaction_classes = [DragPan, KeyboardPan];
    static zoom_interaction_classes = [MouseWheelZoom, PinchZoom, KeyboardZoom];

    constructor() {
        this.map = null;
        this.view = null;
        this.render_scale = 1;

        this.map_settings = { ...MAP_SETTINGS_DEFAULTS };
        this.map_settings_attributes = null;

        this.map_export_settings = { ...MAP_EXPORT_SETTINGS_DEFAULTS };

        this.processing_map_export = false;
        this._is_moving_feature = false;

        this.legend_canvas_elem = null;

        this.marker_source = null;
        this.marker_cluster_source = null;
        this.marker_layer = null;

        this.tracking_source = null;
        this.tracking_layer = null;
        this.tracking_style = null;

        this.base_style_properties = {};
        this.cached_text_fill = null;
        this.cached_text_stroke = null;
        this.highlight_style = null;
        this.cluster_marker_cache = {};
        this.individual_marker_cache = {};

        this.DEFAULT_OUTLINE_COLOR = HexColor.Black;

        this.control_row = { 'left': 0, 'right': 0 };
        this.using_controls = [ // Overridden or modified by child classes
            MapControl.ZoomIn,
            MapControl.ZoomOut,
        ];
        this.control_options = {}; // Overridden or modified by child classes

        this.map_elem_id = 'map';
        this.map_tip = null;

        this.has_custom_context_menu = true;
        this.allow_map_click = new CountdownBarrier();
        this.allow_context_menu = new CountdownBarrier();
        this.allow_pan = new CountdownBarrier(
            () => this._toggleInteractionsByClass(MapPage.pan_interaction_classes, false),
            () => this._toggleInteractionsByClass(MapPage.pan_interaction_classes, true),
        );
        this.allow_zoom = new CountdownBarrier(
            () => this._toggleInteractionsByClass(MapPage.zoom_interaction_classes, false),
            () => this._toggleInteractionsByClass(MapPage.zoom_interaction_classes, true),
        );
    }

    getBaseContextMenu(lng, lat, feature) {
        return [
            { text: 'Actions' },
            {
                text: 'Search for Address',
                action: (() => searchAddressCallback(this.view, this.map_settings['search_location_with_coordinates']==='Yes')),
            },
            {
                text: 'Get Coordinates',
                action: (() => contextMenuCallback_getCoordinates(lng, lat)),
            },
            {
                text: 'Get Nearest Address',
                action: (() => contextMenuCallback_getNearestAddress(lng, lat)),
            },
        ];
    }

    getContextMenu(event) {
        const [ lng, lat, feature ] = this.pixelClick(event);
        return this.getBaseContextMenu(lng, lat, feature);
    }

    pixelClick(event) {
        // Returns [ lng, lat, feature ]
        const pixel = this.map.getEventPixel(event);
        const coordinates = toLonLat(this.map.getCoordinateFromPixel(pixel));
        const lng = coordinates[0];
        const lat = coordinates[1];
        let feature = this.map.forEachFeatureAtPixel(pixel, (feature) => feature);
        if(feature) {
            const features = feature.get('features');
            if(features) { feature = (features.length === 1) ? features[0] : null; }
        }

        return [ lng, lat, feature ];
    }

    async mapSetup() {
        this.view = new View({
            zoom: 19,
            minZoom: 2,
            maxZoom: 23,
        });

        this.map = new Map({
            controls: [
                new AttributionControl(),
            ],
            layers: [
                getBingLayer_satellite(),
                getBingLayer_labels(),
                getGoogleLayer_satellite(),
                getGoogleLayer_labels(),
                getOsmLayer(),
            ],
            interactions: defaultInteractions({
                dragPan: false,
                mouseWheelZoom: false,
                doubleClickZoom: false,
            }),
            target: this.map_elem_id,
            view: this.view,
            moveTolerance: 10,
        });

        this.map_elem = document.getElementById(this.map_elem_id);
    }

    _toggleInteractionsByClass(interaction_classes, state) {
        this.map.getInteractions().forEach((interaction) => {
            if(interaction_classes.some((interaction_class) => interaction instanceof interaction_class)) {
                interaction.setActive(state);
            }
        });
    }

    addControls() {
        for(const control_enum of this.using_controls) {
            if(!control_enum) { continue; }
            const options = this.control_options[control_enum] ?? {};
            options.map_page = this;
            if(!('side' in options)) { options.side = 'left'; }
            if(!('row' in options)) { options.row = ++this.control_row[options.side]; }
            this.map.addControl(new MapControlClass[control_enum](options));
        }

        this.map.addControl(new MapControlClass[MapControl.RotateNorth]({ map_page: this }));
    }

    async setInitialMapCenter() {
        this.view.setCenter(fromLonLat(DEFAULT_MAP_CENTER));
        this.view.setZoom(5);
    }

    showMapTip(text) {
        this.map_tip.textContent = text;
        this.map_tip.classList.remove('hidden');
    }

    hideMapTip() {
        this.map_tip.classList.add('hidden');
    }

    getMapTip(event) {
        const is_mobile_device = isMobileDevice();
        if(!is_mobile_device && event) {
            if(event.shiftKey && !event.ctrlKey) {
                return 'Click and drag to rotate and zoom';
            } else if(event.ctrlKey && !event.shiftKey) {
                return 'Click and drag to zoom into area';
            }
        }
        return null;
    }

    renderMapTip(event) {
        if(modalIsOpen()) { return this.hideMapTip(); }
        if(!document.hasFocus()) { return this.hideMapTip(); }

        const map_tip = this.getMapTip(event);
        if(map_tip) {
            this.showMapTip(map_tip);
        } else {
            this.hideMapTip();
        }
    }

    async main() {
        this.buildBaseStyles();
        this.setupLayers();

        this.map.on('click', (event) => {
            if(this.allow_map_click.isBlocked()) { return; }
            this.onMapClick(event);
        });
        this.map.addLayer(this.line_layer);
        this.map.addLayer(this.marker_layer);
        this.map.addLayer(this.tracking_layer);

        this.map.addInteraction(new DragPan({ condition: leftOrMiddleClickPan }));
        this.map.addInteraction(new MouseWheelZoom({ condition: (event) => !event.originalEvent.ctrlKey }));
        this.map.addInteraction(new DragRotateAndZoom({ condition: shiftKeyOnly }));
        this.map.addInteraction(new DragZoom({ condition: platformModifierKeyOnly }));

        /* Add map interaction tip */ {
            const tip = document.createElement('div');
            tip.classList.add('map-tooltip', 'hidden');
            this.map_elem.appendChild(tip);
            this.map_tip = tip;

            const handleMapTipEvent = ((event) => { this.renderMapTip(event); });
            document.addEventListener('keydown', handleMapTipEvent);
            document.addEventListener('keyup', handleMapTipEvent);
            window.addEventListener('blur', handleMapTipEvent);
            window.addEventListener('focus', handleMapTipEvent);
        }

        /* Add map overlay legend canvas */ {
            this.map_elem.style.position = 'relative';

            this.legend_canvas_elem = this.map_elem.querySelector('#map-overlay-legend-canvas');
            if(!this.legend_canvas_elem) {
                this.legend_canvas_elem = document.createElement('canvas');
                this.legend_canvas_elem.id = 'map-overlay-legend-canvas';
                this.map_elem.appendChild(this.legend_canvas_elem);
            }

            let resize_redraw_legend_timeout;
            const RESIZE_REDRAW_LEGEND_DELAY_MS = 250;
            window.addEventListener('resize', () => {
                clearTimeout(resize_redraw_legend_timeout);
                resize_redraw_legend_timeout = setTimeout(() => {
                    this.renderOverlayLegend();
                }, RESIZE_REDRAW_LEGEND_DELAY_MS);
            });
        }

        /* Add custom context menu */ {
            const custom_context_menu_enabled = (
                'newcollector' in window &&
                'settings' in window.newcollector &&
                'map_context_menu' in window.newcollector.settings &&
                window.newcollector.settings['map_context_menu'] === 'Custom'
            );
            if(this.has_custom_context_menu && custom_context_menu_enabled) {
                /* iPhone context menu polyfill */ {
                    this.map_elem.style.WebkitTouchCallout = 'none';
                    this.map_elem.style.WebkitUserSelect = 'none';
                    this.map_elem.style.userSelect = 'none';

                    let touch_timer;
                    this.map_elem.addEventListener('touchstart', (e) => {
                        if(e.touches.length > 1) {
                            clearTimeout(touch_timer);
                            return;
                        }

                        touch_timer = setTimeout(() => {
                            e.target.dispatchEvent(new MouseEvent('contextmenu', {
                                bubbles: true,
                                cancelable: true,
                                clientX: e.touches[0].clientX,
                                clientY: e.touches[0].clientY
                            }));
                        }, 500);
                    });

                    this.map_elem.addEventListener('touchend', () => clearTimeout(touch_timer));
                    this.map_elem.addEventListener('touchmove', () => clearTimeout(touch_timer));
                }

                ctxmenu.attach('#'+this.map_elem_id, [], {
                    'onBeforeShow': ((menu, event) => {
                        if(this.allow_context_menu.isBlocked()) { return; }
                        this.allow_map_click.acquireBlock('context_menu');
                        return this.getContextMenu(event);
                    }),
                    'onHide': ((menu) => {
                        this.clearContextMenuHighlight();
                        setTimeout(() => { this.allow_map_click.releaseBlock('context_menu'); }, 300);
                    })
                });
            }
        }

        this.addControls();
    }

    /* fully abstract */ clearContextMenuHighlight() {
        console.error('Abstract method not implemented.');
    }

    showLegend() {
        this.legend_canvas_elem.style.display = 'block';
    }

    hideLegend() {
        this.legend_canvas_elem.style.display = 'none';
    }

    getMarkerSize() {
        return this.map_settings['marker_size'] * this.render_scale;
    }

    getMarkerOutlineSize() {
        return this.map_settings['marker_outline_width'] * this.render_scale;
    }

    /* fully abstract */ getStyleForFeature(feature, resolution) {
        console.error('Abstract method not implemented.');
    }

    setupLayers() {
        this.marker_source = new VectorSource();

        this.marker_cluster_source = new Cluster({
            distance: (this.map_settings['clustering']==='On' ? this.map_settings['cluster_distance'] : 0),
            source: this.marker_source,
            geometryFunction: (feature) => {
                const geometry = feature.getGeometry();
                const type = geometry.getType();

                if(type === FeatureType.Point) { return geometry; }
                return null;
            },
        });

        this.line_layer = new VectorLayer({
            name: 'line_layer',
            source: this.marker_source,
            style: (feature, resolution) => {
                const type = feature.getGeometry().getType();
                if(type === FeatureType.Point) { return null; }
                return this.getStyleForFeature(feature, resolution);
            }
        });

        this.marker_layer = new VectorLayer({
            name: 'marker_layer',
            source: this.marker_cluster_source,
            style: (feature, resolution) => {
                const features = feature.get('features');
                const num_features = features.length;

                if(num_features === 1) {
                    return this.getStyleForFeature(features[0], resolution);

                } else {
                    if(num_features in this.cluster_marker_cache) { return this.cluster_marker_cache[num_features]; }

                    const style = new Style({
                        image: new Circle({
                            radius: this.getMarkerSize(),
                            stroke: new Stroke({
                                color: HexColor.White,
                                width: this.getMarkerOutlineSize(),
                            }),
                            fill: new Fill({
                                color: HexColor.MapClusterBlue,
                            }),
                        }),
                        text: new Text({
                            text: num_features.toString(),
                            fill: new Fill({
                                color: (this.map_settings['invert_cluster_text_color']==='Yes' ? HexColor.Black : HexColor.White),
                            }),
                        }),
                    });
                    this.cluster_marker_cache[num_features] = style;
                    return style;

                }
            },
        });

        this.tracking_source = new VectorSource();

        this.tracking_layer = new VectorLayer({
            name: 'tracking_layer',
            source: this.tracking_source,
            style: () => this.tracking_style,
        });
    }

    buildBaseStyles() {
        this.tracking_style = new Style({
            image: new Circle({
                radius: this.getMarkerSize(),
                fill: new Fill({ color: HexColor.MapTrackingBlue }),
                stroke: new Stroke({
                    color: HexColor.White,
                    width: this.getMarkerOutlineSize(),
                }),
            }),
        });

        this.base_style_properties['text_color'] = (this.map_settings['invert_text_color']==='Yes' ? HexColor.White : HexColor.Black);
        this.base_style_properties['text_outline_color'] = (this.map_settings['invert_text_color']==='Yes' ? HexColor.Black : HexColor.White);
        this.base_style_properties['text_outline_thickness'] = (this.map_settings['text_highlight_width'] * this.render_scale);

        this.cached_text_fill = new Fill({
            color: this.base_style_properties['text_color'],
        });

        this.cached_text_stroke = new Stroke({
            color: this.base_style_properties['text_outline_color'],
            width: this.base_style_properties['text_outline_thickness'],
        });

        this.highlight_style = new Style({
            image: new Circle({
                radius: this.getMarkerSize(),
                fill: new Fill({ color: HexColor.MapHighlightOrange }),
                stroke: new Stroke({
                    color: HexColor.White,
                    width: this.getMarkerOutlineSize(),
                }),
            }),
            stroke: new Stroke({
                color: HexColor.MapHighlightOrange,
                width: this.getMarkerOutlineSize() * 2,
            })
        });

        const inner_line_width = this.getMarkerOutlineSize() * 2;
        const outline_thickness = this.getMarkerOutlineSize() * 2.75;
        this.highlight_style_line = [
            new Style({
                stroke: new Stroke({
                    color: HexColor.White,
                    width: inner_line_width + outline_thickness,
                })
            }),
            new Style({
                stroke: new Stroke({
                    color: HexColor.MapHighlightOrange,
                    width: inner_line_width,
                })
            })
        ];

        this.highlight_style_polygon = new Style({
            stroke: new Stroke({
                color: HexColor.White,
                width: this.getMarkerOutlineSize() * 2,
            }),
            fill: new Fill({
                color: hexToRgba(HexColor.MapHighlightOrange, 0.90),
            })
        });
    }

    clearStyleCaches() {
        this.cluster_marker_cache = {};
        this.individual_marker_cache = {};
    }

    renderOverlayLegend() {
        const [mapCanvasWidth, mapCanvasHeight] = this.map.getSize();
        this.legend_canvas_elem.width = mapCanvasWidth;
        this.legend_canvas_elem.height = mapCanvasHeight;

        const ctx = this.legend_canvas_elem.getContext('2d');
        ctx.clearRect(0, 0, mapCanvasWidth, mapCanvasHeight);
    }

    applySettings_mapImagery() {
        this.map.getLayers().forEach((layer) => {
            const type = layer.get('type');
            if(!type) { return; }
            if(type !== 'base') { return; }
            const title = layer.get('title');
            if(!title) { return; }
            if(title === this.map_settings['map_imagery']) {
                layer.setVisible(true);
                layer.setOpacity(this.map_settings['map_imagery_opacity'] / 100);
            } else {
                layer.setVisible(false);
            }
        });
    }

    applySettings() {
        this.marker_source.changed();

        this.marker_cluster_source.setDistance(this.map_settings['clustering']==='On' ? this.map_settings['cluster_distance'] : 0);

        this.applySettings_mapImagery();

        this.map_elem.style.backgroundColor = this.map_settings['map_imagery_background'];

        this.renderOverlayLegend();
        this.buildBaseStyles();
        this.clearStyleCaches();
    }

    /* fully abstract */ async onMapClick(event) {
        console.error('Abstract method not implemented.');
    }

    exportClick() {
        const map_export_settings_modal = new Modal('Generate Map');
        const settings_form_container = document.createElement('div');
        const settings_form = new AssetForm({
            'form_elem': settings_form_container,
            'attributes': MAP_EXPORT_SETTINGS,
            'values': this.map_export_settings,
        });
        settings_form.generateForm();
        map_export_settings_modal.appendNode(settings_form_container);

        const apply_button = document.createElement('button');
        apply_button.classList.add('btn', 'btn-success');
        apply_button.style.display = 'block';
        apply_button.style.width = '100px';
        apply_button.style.margin = 'auto';
        apply_button.innerText = 'Export';
        map_export_settings_modal.appendNode(apply_button);
        apply_button.addEventListener('click', async () => {
            const new_settings = settings_form.validateForm();
            if(new_settings !== false) {
                this.processing_map_export = true;
                this.map_export_settings = new_settings;
                map_export_settings_modal.remove();
                try {
                    this.exportMap();
                } catch(err) {
                    console.error('Error in exportMap: ', err);
                    await delay(500);
                    this.processing_map_export = false;
                    new ErrorModal('<b>Error during map export processing:</b> ' + err.message).show();
                }
            }
        });

        map_export_settings_modal.show();
    }

    /* fully abstract */ drawExportLegend(ctx, map_height, map_width) {
        console.error('Abstract method not implemented.');
    }

    /* fully abstract */ getExportFilename() {
        console.error('Abstract method not implemented.');
    }

    exportMap() {
        const loading_modal = new Modal('Export');
        loading_modal.appendHtml('<p>Readying map for export...</p>');
        loading_modal.show();

        const pixel_ratio = window.devicePixelRatio || 1;
        const original_size = [...this.map.getSize()];
        const original_resolution = this.map.getView().getResolution();
        const original_center = [...this.map.getView().getCenter()];

        let export_container;
        let [map_width, map_height] = original_size;
        let scale_factor;
        let print_width;
        let print_height;

        /* Set map aspect ratio */ {
            const aspect_ratio = parseIntStrict(this.map_export_settings['image_aspect_ratio']);
            const dimensions_based_on = parseIntStrict(this.map_export_settings['image_dimensions_based_on']);
            const image_resolution = parseIntStrict(this.map_export_settings['image_resolution']);
            assertIncludes([1,2,3,4,5], aspect_ratio);
            assertIncludes([1,2], dimensions_based_on);
            assertIncludes([1,2,3], image_resolution);

            const target_dpi = ({
                1: 150,
                2: 300,
                3: 600,
            })[image_resolution];

            const [target_width_in, target_height_in] = ({
                1: [8.5, 11], // Portrait (8.5 x 11)
                2: [11, 8.5], // Landscape (11 x 8.5)
                3: [11, 11], // Square (11 x 11)
                4: [11, 17], // Portrait (11 x 17)
                5: [17, 11], // Landscape (17 x 11)
            })[aspect_ratio];

            const target_aspect = target_width_in / target_height_in;
            const target_width_px = target_width_in * target_dpi;
            const target_height_px = target_height_in * target_dpi;
            const derived_height = map_width / target_aspect;
            const derived_width = map_height * target_aspect;

            if(dimensions_based_on === 1) {
                // Based on browser width
                scale_factor = target_width_px / map_width;
                print_width = Math.round(target_width_px); // Essentially matches target_width_px
                print_height = Math.round(derived_height * scale_factor);

            } else if(dimensions_based_on === 2) {
                // Based on browser height
                scale_factor = target_height_px / map_height;
                print_width = Math.round(derived_width * scale_factor);
                print_height = Math.round(target_height_px);
            }

            export_container = document.createElement('div');
            export_container.style.width = Math.round(print_width / pixel_ratio) + 'px';
            export_container.style.height = Math.round(print_height / pixel_ratio) + 'px';
            export_container.style.position = 'absolute';
            export_container.style.top = '-9999px';
            document.body.appendChild(export_container);
        }

        const cleanupMap = () => {
            this.map.setTarget(this.map_elem_id);
            this.map.setSize(original_size);
            this.map.getView().setResolution(original_resolution);
            this.map.getView().setCenter(original_center);

            this.render_scale = 1;
            this.buildBaseStyles();
            this.clearStyleCaches();
            this.marker_cluster_source.changed();

            loading_modal.remove();
            document.body.removeChild(export_container);
        }

        const renderCompleteCallback = (async (event) => {
            let did_fail_loading_img = false;
            try {
                clearTimeout(exportTimeout);
                const export_canvas = document.createElement('canvas');
                export_canvas.width = Math.round(print_width);
                export_canvas.height = Math.round(print_height);
                const ctx = export_canvas.getContext('2d');

                ctx.fillStyle = this.map_settings['map_imagery_background'];
                ctx.fillRect(0, 0, export_canvas.width, export_canvas.height);

                const ol_canvas = this.map.getViewport().querySelector('canvas');
                if(!ol_canvas) { throw Error('Could not get ol_canvas'); }
                ctx.drawImage(ol_canvas, 0, 0);

                const OVERLAY_MARGIN = 20;
                const OVERLAY_BACKGROUND_COLOR = 'rgba(255, 255, 255, 0.8)';

                /* Add title box */ {
                    const TITLE_BOX_WIDTH = 580;
                    const LOGO_WIDTH = 250;
                    const LOGO_PADDING = 15;
                    const LOGO_SECTION_HEIGHT = 140; // Used for positioning text below logo section
                    const TITLE_FONT = 'bold 35px Arial';
                    const SUBTITLE_FONT = '30px Arial';

                    const num_subtitles = (this.map_export_settings['subtitles'] || []).length;
                    const title_box_height = 175 + (30 * num_subtitles) + (num_subtitles>0 ? 20 : 0);
                    const horizontal_centerpoint = OVERLAY_MARGIN + (TITLE_BOX_WIDTH / 2);

                    // Title box background
                    ctx.textBaseline = 'middle';
                    ctx.textAlign = 'center';
                    ctx.fillStyle = OVERLAY_BACKGROUND_COLOR;
                    ctx.fillRect(OVERLAY_MARGIN, OVERLAY_MARGIN, TITLE_BOX_WIDTH, title_box_height);


                    /* Add davey logo */ {
                        const img = await loadImage('static/Davey_Tree_logo.png');
                        if(img !== null) {
                            const logo_aspect_ratio = img.naturalWidth / img.naturalHeight;
                            ctx.drawImage(img, OVERLAY_MARGIN + LOGO_PADDING, OVERLAY_MARGIN + LOGO_PADDING, LOGO_WIDTH, LOGO_WIDTH / logo_aspect_ratio);
                        } else {
                            did_fail_loading_img = true;
                        }
                    }

                    /* Add scale line */ {
                        const SCALE_LINE_WIDTH = 200; // Target width in pixels. Distance is rounded and width is adjusted to match.
                        const DISTANCE_TEXT_ABOVE_LINE = 15; // Vertical offset for distance numbers over line
                        const VERTICAL_PADDING = 60; // Padding above scale line
                        const HORIZONTAL_PADDING = 30; // Padding between logo and scale line

                        const view = this.map.getView();
                        const point_resolution = getPointResolution(view.getProjection(), view.getResolution(), view.getCenter());
                        const canvas_point_resolution = point_resolution / pixel_ratio;
                        const meters_per_n_px = SCALE_LINE_WIDTH * canvas_point_resolution;
                        const x = OVERLAY_MARGIN + LOGO_WIDTH + (LOGO_PADDING * 2) + HORIZONTAL_PADDING;
                        const y = OVERLAY_MARGIN + LOGO_PADDING + VERTICAL_PADDING;

                        let rounded_distance;
                        let rounded_scale_line_width;
                        if(meters_per_n_px < 50) {
                            rounded_distance = Math.round(meters_per_n_px / 10) * 10;
                            rounded_scale_line_width = rounded_distance / canvas_point_resolution;
                        } else {
                            rounded_distance = Math.round(meters_per_n_px / 50) * 50;
                            rounded_scale_line_width = rounded_distance / canvas_point_resolution;
                        }

                        ctx.strokeStyle = HexColor.Black;
                        ctx.lineWidth = 3;
                        ctx.beginPath();
                        ctx.moveTo(x, y);
                        ctx.lineTo(x + rounded_scale_line_width, y);
                        ctx.stroke();

                        ctx.fillStyle = HexColor.Black;
                        ctx.font = "25px Arial";
                        ctx.fillText('0m', x, y - DISTANCE_TEXT_ABOVE_LINE);
                        ctx.fillText(rounded_distance+'m', x + rounded_scale_line_width, y - DISTANCE_TEXT_ABOVE_LINE);
                    }

                    /* Add text */ {
                        ctx.fillStyle = HexColor.DaveyGreenDark;

                        ctx.font = TITLE_FONT;
                        ctx.fillText(this.map_export_settings['title'] || 'Map Export', horizontal_centerpoint, OVERLAY_MARGIN + LOGO_SECTION_HEIGHT, TITLE_BOX_WIDTH);

                        ctx.font = SUBTITLE_FONT;
                        for(let i=0; i<num_subtitles; i++) {
                            ctx.fillText(this.map_export_settings['subtitles'][i], horizontal_centerpoint, OVERLAY_MARGIN + LOGO_SECTION_HEIGHT + 50 + (i*30), TITLE_BOX_WIDTH);
                        }
                    }

                }

                /* Add north indicator */ {
                    const NORTH_INDICATOR_WIDTH = 175;
                    if(!did_fail_loading_img) {
                        const img = await loadImage('static/icons/north.webp');
                        if(img !== null) {
                            const north_indicator_aspect_ratio = img.naturalWidth / img.naturalHeight;
                            const north_indicator_height = NORTH_INDICATOR_WIDTH / north_indicator_aspect_ratio;
                            const x = export_canvas.width - (OVERLAY_MARGIN + NORTH_INDICATOR_WIDTH);
                            const y = OVERLAY_MARGIN;
                            const rotation_radians = this.map.getView().getRotation();

                            if(rotation_radians === 0) {
                                ctx.drawImage(img, x, y, NORTH_INDICATOR_WIDTH, north_indicator_height);

                            } else {
                                const cx = export_canvas.width - OVERLAY_MARGIN - (NORTH_INDICATOR_WIDTH / 2);
                                const cy = OVERLAY_MARGIN + (north_indicator_height / 2);

                                rotateCanvasAboutPoint(ctx, rotation_radians, cx, cy);
                                ctx.drawImage(img, x, y, NORTH_INDICATOR_WIDTH, north_indicator_height);
                                rotateCanvasAboutPoint(ctx, -rotation_radians, cx, cy);
                            }
                        } else {
                            did_fail_loading_img = true;
                        }
                    }
                }

                /* Add attributions */ {
                    try {
                        const ATTRIBUTION_FONT = '14px Arial';
                        const ATTRIBUTION_PADDING = 5; // Padding around attribution text

                        const current_image_layer = this.map.getLayers().getArray().find((layer) => {
                            return layer.get('type') === 'base' && layer.get('title') === this.map_settings['map_imagery'];
                        });
                        if(!current_image_layer) {
                            console.error('Could not find current_image_layer');
                        } else {
                            const getAttributionsFunc = current_image_layer.getSource().getAttributions();
                            if(!getAttributionsFunc) {
                                console.error('Attributions func was falsy');
                            } else {
                                let attribution_text_debug = '';
                                let attribution_text = getAttributionsFunc(event.frameState);
                                if(attribution_text instanceof Promise) {
                                    attribution_text = await attribution_text;
                                    attribution_text_debug += 'attribution_text was instanceof Promise; ';
                                }

                                const stripHTML = (htmlString) => {
                                    return htmlString ? htmlString.replace(/<[^>]*>?/gm, '').trim() : '';
                                };

                                if(Array.isArray(attribution_text)) {
                                    attribution_text = attribution_text.map(stripHTML).filter(item => item !== '')
                                    attribution_text = attribution_text.join(' ');
                                    attribution_text_debug += 'attribution_text was Array; ';

                                } else if(typeof attribution_text === 'string') {
                                    attribution_text = stripHTML(attribution_text);
                                    attribution_text_debug += 'attribution_text was string; ';

                                } else {
                                    console.error('Unhandled attribution_text type `'+(typeof attribution_text)+'`');
                                    attribution_text = '';
                                    attribution_text_debug += 'attribution_text was unhandled type '+typeof attribution_text+'; ';
                                }

                                if(attribution_text==='') {
                                    if(this.map_settings['map_imagery']==='OpenStreetMap') {
                                        attribution_text = '© OpenStreetMap contributors';
                                    } else if(this.map_settings['map_imagery'] === 'Google') {
                                        attribution_text = 'Map data © Google';
                                    } else {
                                        console.error('Attribution text was blank for '+this.map_settings['map_imagery']+' (debug: '+attribution_text_debug+')');
                                        attribution_text = 'Map Data: '+this.map_settings['map_imagery'];
                                    }
                                }

                                ctx.font = ATTRIBUTION_FONT;
                                const attribution_size = ctx.measureText(attribution_text);
                                const attribution_width = attribution_size.width + (ATTRIBUTION_PADDING * 2);
                                const attribution_text_height = (attribution_size.actualBoundingBoxAscent + attribution_size.actualBoundingBoxDescent);
                                const attribution_height = attribution_text_height + (ATTRIBUTION_PADDING * 2);

                                const x = export_canvas.width - (attribution_width + OVERLAY_MARGIN);
                                const y = export_canvas.height - (attribution_height + OVERLAY_MARGIN);

                                ctx.fillStyle = OVERLAY_BACKGROUND_COLOR;
                                ctx.fillRect(x, y, attribution_width, attribution_height);

                                ctx.fillStyle = HexColor.Black;
                                ctx.textAlign = 'left';
                                ctx.textBaseline = 'middle';
                                ctx.fillText(attribution_text, x + ATTRIBUTION_PADDING, y + (attribution_height / 2));
                            }
                        }
                    } catch(err) {
                        console.log('Loading attributions failed.');
                        sentryCaptureException(err);
                    }
                }

                /* Create legend for marker colors (if any implemented) */
                this.drawExportLegend(ctx, export_canvas.height, export_canvas.width);

                const link = document.createElement('a');
                link.download = this.getExportFilename();
                link.href = export_canvas.toDataURL();
                link.click();

            } catch(err) {
                console.log('Exception in map.once rendercomplete: ', err);
                sentryCaptureException(err);
                new ErrorModal('Error exporting map, try again.').show();

            } finally {
                cleanupMap();

                await delay(500);
                this.processing_map_export = false;
            }
        });

        this.map.once('rendercomplete', renderCompleteCallback);

        const exportTimeout = setTimeout(() => {
            this.map.un('rendercomplete', renderCompleteCallback);

            cleanupMap();
            this.map.setTarget(this.map_elem_id);

            this.processing_map_export = false;

            new ErrorModal('<b>Export Timeout:</b> The map took too long to load, try again.').show();
            console.error('Hit timeout on renderCompleteCallback in exportMap');
        }, 30000);

        const resolution_scale = scale_factor / pixel_ratio; // Counteracts OpenLayers' internal behavior of multiplying canvas strokes/fonts by devicePixelRatio.
        this.render_scale = resolution_scale / pixel_ratio;
        this.buildBaseStyles();
        this.clearStyleCaches();
        this.marker_cluster_source.changed();

        // Scale map up to get high resolution image for printing
        this.map.setSize([Math.round(print_width / pixel_ratio), Math.round(print_height / pixel_ratio)]);
        this.map.getView().setResolution(original_resolution / resolution_scale);

        this.map.setTarget(export_container);
    }
}